import { SAJU_CHARTS, SAJU_PRODUCTS, SAJU_SERIES_PAGES } from './template-saju-catalog.mjs';
import { SAJU_BOOK_FRAME } from './template-saju-book.mjs';
import { SAJU_PRODUCT_COPY } from './template-saju-copy.mjs';

// Example ids are prefixed: the Saju catalog also has a `wealth-flow` product,
// which must not collide with the existing `wealth-flow` editorial sample.
export const SAJU_PRODUCT_EXAMPLE_PREFIX = 'product-';
export const sajuProductExampleId = (productId) => `${SAJU_PRODUCT_EXAMPLE_PREFIX}${productId}`;

export const SAJU_PRODUCT_EXAMPLES = Object.freeze(SAJU_PRODUCTS.map(product => Object.freeze({
  id: sajuProductExampleId(product.id), productId: product.id, label: `${product.title} · 사주 상품 템플릿`,
})));

export function getSajuProduct(productId) {
  const product = SAJU_PRODUCTS.find(entry => entry.id === productId);
  if (!product) throw new Error(`등록되지 않은 사주 상품입니다: ${productId}`);
  return product;
}

/**
 * The editable EduSense document (schemaVersion 1, saju-editorial) for one
 * product: the same pages the report builder lays out, as sections.
 */
export function createSajuProductDocument(productId) {
  const product = getSajuProduct(productId);
  const copy = SAJU_PRODUCT_COPY[product.id];
  const sections = [
    { id: 'product-cover', kind: 'text', title: copy.heading, body: copy.lead },
    { id: 'product-beats', kind: 'cards', title: '핵심 특징', items: copy.beats.map(beat => ({ title: beat.title, body: beat.text })) },
    // The chart cards the book shows, as the sample chart's picture: the Studio has no chart
    // components, and a template has no reader. The book draws the reader's own chart here.
    ...product.charts.map(kind => ({
      id: `product-chart-${kind}`, kind: 'image', title: SAJU_CHARTS[kind].heading, assetId: `saju-chart-${kind}`,
      alt: `${SAJU_CHARTS[kind].heading} (표본 명식)`,
      body: `${SAJU_CHARTS[kind].body} 표본 명식(윤석규)의 차트이며, 책에서는 독자의 명식으로 다시 그립니다.`,
    })),
    // Each page's heading is the one the book prints on it (a frame word, template-saju-book.mjs).
    { id: 'product-mechanism', kind: 'text', title: SAJU_BOOK_FRAME['mechanism.1'].text, body: `${copy.mechanism}\n\n${copy.scene}` },
    { id: 'product-comparison', kind: 'text', title: SAJU_BOOK_FRAME['comparison.1'].text, body: copy.comparison },
    { id: 'product-advice', kind: 'quote', title: SAJU_BOOK_FRAME['advice.1'].text, body: copy.advice },
    { id: 'product-evidence', kind: 'text', title: '해설 근거', body: copy.evidence },
  ];
  const series = SAJU_SERIES_PAGES[product.id];
  if (series) {
    sections.push({ id: 'product-series', kind: 'text', title: '이어지는 시리즈 페이지',
      body: `이 상품은 뒤에 ${series}가 이어집니다. 해당 페이지는 사주 계산 데이터로 생성되므로 템플릿에는 구성만 표시합니다.` });
  }
  return {
    schemaVersion: 1, templateId: 'saju-editorial', title: product.title, subtitle: copy.question,
    kicker: '사주 상품 템플릿 · 개인화 편집 샘플',
    footer: '전통 명리의 상징 해설 · 사건·성과·확률의 예측이 아닙니다.',
    sections,
  };
}

const SAJU_EDITABLE_FIELDS = Object.freeze(['heading', 'lead', 'question', 'beats', 'mechanism', 'scene', 'comparison', 'advice']);

/**
 * Template sections whose text the book does not take from this document: it computes it from the
 * chart (해설 근거), keeps it with every book that shows that chart (the chart cards' text), or does
 * not print it (the series note). The Studio shows their pages read only, with this reason, and
 * never reads them back, so an edit cannot quietly go nowhere.
 */
const CHART_TEXT = '차트 설명 · 읽기 전용 — 이 차트를 싣는 책들이 함께 쓰는 카탈로그 글입니다. 책 쪽으로 편집에서 이 차트의 쪽을 열어 고칩니다.';
export const SAJU_READ_ONLY_SECTIONS = Object.freeze({
  'product-evidence': '계산 근거 · 읽기 전용 — 책의 원문 근거는 명식 데이터로 계산해 넣습니다. 여기서 고쳐도 책은 바뀌지 않아 편집을 막았습니다.',
  ...Object.fromEntries(Object.keys(SAJU_CHARTS).map(kind => [`product-chart-${kind}`, CHART_TEXT])),
  'product-series': '시리즈 안내 · 읽기 전용 — 이 글은 책에 들어가지 않습니다. 책의 대운·세운·월운 쪽은 책 쪽으로 편집에서 그 쪽을 열어 고칩니다.',
});

/**
 * Text on the product's own pages the book does not take from this document: the product's name
 * (how a Studio document finds its product), the Studio's kicker and footer, and each page's heading
 * (the book's frame words). The canvas keeps them locked; the note says where they are edited.
 */
export const SAJU_LOCKED_ROLES = Object.freeze(['document.kicker', 'document.title', 'document.footer', 'product-beats.title',
  'product-mechanism.title', 'product-comparison.title', 'product-advice.title']);
export const SAJU_LOCKED_NOTE = '잠긴 글(상품 이름·쪽 제목·머리말)은 책이 함께 쓰는 글이라 여기서 고치지 않습니다. 쪽 제목은 책 쪽으로 편집에서 그 쪽을 열어 고치고, 상품 이름은 상품 목록에서 바꿉니다.';

function surfaceTexts(snapshot) {
  const texts = new Map();
  const visit = (block) => {
    if (!block || typeof block !== 'object') return;
    for (const [id, element] of Object.entries(block.props?.elements ?? {})) {
      const delta = element?.text?.delta;
      if (element?.type === 'text' && Array.isArray(delta)) texts.set(id, delta.map(part => (typeof part.insert === 'string' ? part.insert : '')).join(''));
    }
    for (const child of block.children ?? []) visit(child);
  };
  visit(snapshot?.blocks);
  return texts;
}

/**
 * Read one Saju product's copy back from a Studio document, so text edited in the
 * Studio is what the Saju sample book prints. Accepts the editorial document
 * (schemaVersion 1, sections by id) or a canvas made from it (schemaVersion 2/3,
 * whose section carries `origin`: which template field each text element came
 * from). Fields the document no longer holds keep the module copy; `apply` follows
 * `scene` where the two were the same text.
 *
 * Returns { productId, copy, edited, kept }: edited/kept list the field names that
 * came from the document and from the module.
 */
export function sajuCopyFromStudioDocument(document) {
  let fields = new Map();
  let title;
  if (document?.schemaVersion === 1) {
    title = document.title;
    const set = (role, value) => { if (typeof value === 'string') fields.set(role, value); };
    set('document.subtitle', document.subtitle);
    for (const section of document.sections ?? []) {
      set(`${section.id}.title`, section.title);
      set(`${section.id}.body`, section.body);
      (section.items ?? []).forEach((item, index) => { set(`${section.id}.items.${index}.title`, item.title); set(`${section.id}.items.${index}.body`, item.body); });
    }
  } else {
    // A product opens as one canvas page per section; each page carries its part of the origin.
    const sections = (document?.sections ?? []).filter(entry => entry?.origin?.templateId === 'saju-editorial');
    if (!sections.length) throw new Error('사주 상품 템플릿에서 만든 제작실 문서가 아닙니다(원본 정보가 없습니다).');
    const titles = new Set(sections.map(section => section.origin.sourceTitle));
    if (titles.size !== 1) throw new Error(`한 문서에 여러 사주 상품이 섞여 있습니다: ${[...titles].join(', ')}`);
    title = sections[0].origin.sourceTitle;
    for (const section of sections) {
      const texts = surfaceTexts(section.snapshot);
      for (const [id, role] of Object.entries(section.origin.elements)) if (texts.has(id)) fields.set(role, texts.get(id));
    }
  }
  // The product is identified by its title at conversion time (origin.sourceTitle): renaming a
  // product in the catalog leaves canvases made before the rename unmatched (an error, not a guess).
  const product = SAJU_PRODUCTS.find(entry => entry.title === title);
  if (!product) throw new Error(`사주 상품을 찾을 수 없습니다: ${title}`);
  const original = SAJU_PRODUCT_COPY[product.id];
  const read = (role) => (fields.has(role) ? fields.get(role).trim() : undefined);
  const next = {};
  next.heading = read('product-cover.title');
  next.lead = read('product-cover.body');
  next.question = read('document.subtitle');
  const mechanismBody = read('product-mechanism.body');
  if (mechanismBody !== undefined) {
    const split = mechanismBody.indexOf('\n\n');
    next.mechanism = (split < 0 ? mechanismBody : mechanismBody.slice(0, split)).trim();
    next.scene = split < 0 ? undefined : mechanismBody.slice(split + 2).trim();
  }
  next.comparison = read('product-comparison.body');
  next.advice = read('product-advice.body');
  if (original.beats.some((_, index) => fields.has(`product-beats.items.${index}.title`) || fields.has(`product-beats.items.${index}.body`))) {
    next.beats = original.beats.map((beat, index) => ({
      title: read(`product-beats.items.${index}.title`) ?? beat.title,
      text: read(`product-beats.items.${index}.body`) ?? beat.text,
    }));
  }
  const copy = { ...original };
  const edited = [], kept = [];
  for (const field of SAJU_EDITABLE_FIELDS) {
    if (next[field] === undefined || next[field] === '') { kept.push(field); continue; }
    copy[field] = next[field];
    if (JSON.stringify(next[field]) !== JSON.stringify(original[field])) edited.push(field);
  }
  if (original.apply === original.scene) copy.apply = copy.scene;
  return { productId: product.id, copy, edited, kept };
}

// A product field by the address a book page's part names it (doc/heading, doc/beats/0/title,
// doc/scene): the Studio role of the canvas element that holds it. 원인 and 장면 share one element,
// 원인 first, then a blank line, then 장면 (sajuCopyFromStudioDocument reads them apart the same way).
const FIELD_ROLES = Object.freeze({ heading: 'product-cover.title', lead: 'product-cover.body', question: 'document.subtitle',
  mechanism: 'product-mechanism.body', scene: 'product-mechanism.body', comparison: 'product-comparison.body', advice: 'product-advice.body' });
const DOC_FIELD = /^doc\/(?:(heading|lead|question|mechanism|scene|comparison|advice)|beats\/([0-2])\/(title|text))$/;

function docField(address) {
  const match = DOC_FIELD.exec(address);
  if (!match) throw new Error(`not a product field: ${address}`);
  const [, field, beat, part] = match;
  return field ? { field, role: FIELD_ROLES[field] } : { field: 'beats', beat: Number(beat), part, role: `product-beats.items.${beat}.${part === 'title' ? 'title' : 'body'}` };
}

/** Whether an address names a product field of a Saju Studio document. */
export const isSajuDocField = address => DOC_FIELD.test(String(address));
/** Every product field's address, in the order the book prints them. */
export const SAJU_DOC_FIELDS = Object.freeze(['doc/heading', 'doc/lead', 'doc/question',
  ...[0, 1, 2].flatMap(beat => [`doc/beats/${beat}/title`, `doc/beats/${beat}/text`]),
  'doc/mechanism', 'doc/scene', 'doc/comparison', 'doc/advice']);

/**
 * A product field's text as the Studio document has it now (what the book will print from it), or
 * with `original` as the product's template has it (what the published book printed).
 */
export function sajuDocText(document, address, { original = false } = {}) {
  const { field, beat, part } = docField(address);
  const { productId, copy } = sajuCopyFromStudioDocument(document);
  const source = original ? SAJU_PRODUCT_COPY[productId] : copy;
  return field === 'beats' ? source.beats[beat][part] : source[field];
}

/**
 * Writes a product field's text into the canvas element that holds it, in place (the document
 * must not be open in an editor, which would write its own text back over it). Throws when no
 * element of the document holds the field.
 */
export function setSajuDocText(document, address, value) {
  const { field, role } = docField(address);
  const text = String(value).trim();
  if (!text) throw new Error('빈 글로 바꿀 수 없습니다.');
  for (const section of document.sections ?? []) {
    const id = Object.entries(section.origin?.elements ?? {}).find(([, name]) => name === role)?.[0];
    if (!id) continue;
    let element = null;
    const visit = block => {
      if (element || !block || typeof block !== 'object') return;
      if (block.props?.elements?.[id]) element = block.props.elements[id];
      for (const child of block.children ?? []) visit(child);
    };
    visit(section.snapshot?.blocks);
    if (!element?.text || !Array.isArray(element.text.delta)) continue;
    let next = text;
    if (field === 'mechanism' || field === 'scene') {
      const body = element.text.delta.map(op => (typeof op.insert === 'string' ? op.insert : '')).join('');
      const split = body.indexOf('\n\n');
      const [mechanism, scene] = split < 0 ? [body, ''] : [body.slice(0, split), body.slice(split + 2)];
      next = field === 'mechanism' ? [text, scene].filter(Boolean).join('\n\n') : `${mechanism.trim()}\n\n${text}`;
    }
    element.text.delta = [{ insert: next }];
    return;
  }
  throw new Error(`이 문서에는 ${address} 를 담은 글이 없습니다.`);
}

/** Pull the content document out of a Studio export (fenced `edusense-content` Markdown or raw JSON). */
export function parseStudioExport(text) {
  const fenced = /```edusense-content\s*\n([\s\S]*?)\n```/.exec(text);
  return JSON.parse(fenced ? fenced[1] : text);
}
